import React from 'react';
import './Store.css';

interface StoreProps {
  isOpen: boolean;
  onClose: () => void;
  coins: number;
  upgrades: {
    happiness: number;
    productivity: number;
    boulderSize: number;
  };
  onUpgrade: (upgradeType: 'happiness' | 'productivity' | 'boulderSize') => void;
  isAchievementsUnlocked: boolean;
  isAutomationUnlocked: boolean;
  mahjongPurchased: boolean;
  isGamblingUnlocked: boolean;
  isMicroUnlocked: boolean;
  isRockMarketUnlocked: boolean;
  isCustomizationUnlocked: boolean;
  isFarmUnlocked: boolean;
  onPurchaseFeature: (feature: 'achievements' | 'automation' | 'mahjong' | 'gambling' | 'microtransactions' | 'rockMarket' | 'customization' | 'farm') => void;
}

const Store: React.FC<StoreProps> = ({ isOpen, onClose, coins, upgrades, onUpgrade, isAchievementsUnlocked, isAutomationUnlocked, mahjongPurchased, isGamblingUnlocked, isMicroUnlocked, isRockMarketUnlocked, isCustomizationUnlocked, isFarmUnlocked, onPurchaseFeature }) => {
  if (!isOpen) return null;

  const getUpgradeCost = (level: number): number => {
    return Math.floor(10 * Math.pow(1.5, level));
  };

  const canAfford = (level: number): boolean => {
    return coins >= getUpgradeCost(level);
  };

  const isMaxLevel = (level: number): boolean => {
    return level >= 10;
  };

  return (
    <div className="store-overlay" onClick={onClose}>
      <div className="store-popup" onClick={(e) => e.stopPropagation()}>
        <button className="store-close-button" onClick={onClose}>×</button>
        
        <div className="store-header">
          <h2 className="store-title">The Happy Store</h2>
          <p className="store-subtitle">Best bouldering supplies on this side of Tartarus!</p>
        </div>

        <div className="store-upgrades">
          {/* Happiness Upgrade */}
          <div className="upgrade-item">
            <div className="upgrade-header">
              <span className="upgrade-icon">😊</span>
              <div className="upgrade-info">
                <h3>Happiness</h3>
                <p className="upgrade-description">Level {upgrades.happiness}/10</p>
              </div>
            </div>
            <button
              className={`upgrade-button ${isMaxLevel(upgrades.happiness) ? 'max-level' : canAfford(upgrades.happiness) ? 'affordable' : 'unaffordable'}`}
              onClick={() => onUpgrade('happiness')}
              disabled={isMaxLevel(upgrades.happiness) || !canAfford(upgrades.happiness)}
            >
              {isMaxLevel(upgrades.happiness) 
                ? 'MAX LEVEL' 
                : `Upgrade (${getUpgradeCost(upgrades.happiness)} 🪙)`}
            </button>
          </div>

          {/* Productivity Upgrade */}
          <div className="upgrade-item">
            <div className="upgrade-header">
              <span className="upgrade-icon">⚡</span>
              <div className="upgrade-info">
                <h3>Productivity</h3>
                <p className="upgrade-description">Level {upgrades.productivity}/10 • +{upgrades.productivity} coins per ascension</p>
              </div>
            </div>
            <button
              className={`upgrade-button ${isMaxLevel(upgrades.productivity) ? 'max-level' : canAfford(upgrades.productivity) ? 'affordable' : 'unaffordable'}`}
              onClick={() => onUpgrade('productivity')}
              disabled={isMaxLevel(upgrades.productivity) || !canAfford(upgrades.productivity)}
            >
              {isMaxLevel(upgrades.productivity) 
                ? 'MAX LEVEL' 
                : `Upgrade (${getUpgradeCost(upgrades.productivity)} 🪙)`}
            </button>
          </div>

          {/* Boulder Size Upgrade */}
          <div className="upgrade-item">
            <div className="upgrade-header">
              <span className="upgrade-icon">🪨</span>
              <div className="upgrade-info">
                <h3>Boulder Size</h3>
                <p className="upgrade-description">Level {upgrades.boulderSize}/10 • +{upgrades.boulderSize} score per ascension</p>
              </div>
            </div>
            <button
              className={`upgrade-button ${isMaxLevel(upgrades.boulderSize) ? 'max-level' : canAfford(upgrades.boulderSize) ? 'affordable' : 'unaffordable'}`}
              onClick={() => onUpgrade('boulderSize')}
              disabled={isMaxLevel(upgrades.boulderSize) || !canAfford(upgrades.boulderSize)}
            >
              {isMaxLevel(upgrades.boulderSize) 
                ? 'MAX LEVEL' 
                : `Upgrade (${getUpgradeCost(upgrades.boulderSize)} 🪙)`}
            </button>
          </div>

          {/* Features Section */}
          <div className="store-features-section">
            <h3 className="features-section-title">Special Features</h3>
            
            {/* Achievements */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">🏆</span>
                <div className="feature-info">
                  <h3>Achievements</h3>
                  <p className="feature-description">
                    {isAchievementsUnlocked 
                      ? 'Unlocked! Track your progress and milestones.' 
                      : 'Unlock the achievements system to track your progress!'}
                  </p>
                </div>
              </div>
              {!isAchievementsUnlocked && (
                <button
                  className={`feature-button ${coins >= 50 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('achievements')}
                  disabled={coins < 50}
                >
                  Purchase (50 🪙)
                </button>
              )}
              {isAchievementsUnlocked && (
                <button className="feature-button max-level" disabled>
                  Unlocked ✓
                </button>
              )}
            </div>

            {/* Automation */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">⚙️</span>
                <div className="feature-info">
                  <h3>Automation</h3>
                  <p className="feature-description">
                    {isAutomationUnlocked 
                      ? 'Unlocked! Automate your boulder pushing.' 
                      : 'Unlock automation to push boulders automatically!'}
                  </p>
                </div>
              </div>
              {!isAutomationUnlocked && (
                <button
                  className={`feature-button ${coins >= 100 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('automation')}
                  disabled={coins < 100}
                >
                  Purchase (100 🪙)
                </button>
              )}
              {isAutomationUnlocked && (
                <button className="feature-button max-level" disabled>
                  Unlocked ✓
                </button>
              )}
            </div>

            {/* Gambling Addiction unlock */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">🎰</span>
                <div className="feature-info">
                  <h3>Gambling Addiction</h3>
                  <p className="feature-description">
                    {isGamblingUnlocked
                      ? 'Unlocked! Play the slot machine and try your luck.'
                      : 'Unlock a cursed slot machine that eats your coins (and sometimes gives them back).'}
                  </p>
                </div>
              </div>
              {!isGamblingUnlocked && (
                <button
                  className={`feature-button ${coins >= 100 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('gambling')}
                  disabled={coins < 100}
                >
                  Purchase (100 🪙)
                </button>
              )}
              {isGamblingUnlocked && (
                <button className="feature-button max-level" disabled>
                  Unlocked ✓
                </button>
              )}
            </div>

            {/* Microtransactions unlock */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">💎</span>
                <div className="feature-info">
                  <h3>Microtransactions</h3>
                  <p className="feature-description">
                    {isMicroUnlocked
                      ? 'Unlocked! Buy SHINY ROCKS and waste them on emotional upgrades.'
                      : 'Unlock access to SHINY ROCKS and premium feelings.'}
                  </p>
                </div>
              </div>
              {!isMicroUnlocked && (
                <button
                  className={`feature-button ${coins >= 1000 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('microtransactions')}
                  disabled={coins < 1000}
                >
                  Purchase (1000 🪙)
                </button>
              )}
              {isMicroUnlocked && (
                <button className="feature-button max-level" disabled>
                  Unlocked ✓
                </button>
              )}
            </div>

            {/* Rock Market unlock */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">📈</span>
                <div className="feature-info">
                  <h3>Rock Market</h3>
                  <p className="feature-description">
                    {isRockMarketUnlocked
                      ? 'Unlocked! Buy and sell Rocks on the stock market.'
                      : 'Unlock access to the Rock Market and trade Rocks for coins.'}
                  </p>
                </div>
              </div>
              {!isRockMarketUnlocked && (
                <button
                  className={`feature-button ${coins >= 1000 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('rockMarket')}
                  disabled={coins < 1000}
                >
                  Purchase (1000 🪙)
                </button>
              )}
              {isRockMarketUnlocked && (
                <button className="feature-button max-level" disabled>
                  Unlocked ✓
                </button>
              )}
            </div>

            {/* Customization unlock */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">🎨</span>
                <div className="feature-info">
                  <h3>Customization</h3>
                  <p className="feature-description">
                    {isCustomizationUnlocked
                      ? 'Unlocked! Customize your boulder with different colors and styles.'
                      : 'Unlock the customization menu to personalize your boulder!'}
                  </p>
                </div>
              </div>
              {!isCustomizationUnlocked && (
                <button
                  className={`feature-button ${coins >= 200 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('customization')}
                  disabled={coins < 200}
                >
                  Purchase (200 🪙)
                </button>
              )}
              {isCustomizationUnlocked && (
                <button className="feature-button max-level" disabled>
                  Unlocked ✓
                </button>
              )}
            </div>

            {/* Farm unlock */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">🌾</span>
                <div className="feature-info">
                  <h3>Farm</h3>
                  <p className="feature-description">
                    {isFarmUnlocked
                      ? 'Unlocked! Grow crops and harvest them for profit!'
                      : 'Unlock the farm to grow crops and make coins!'}
                  </p>
                </div>
              </div>
              {!isFarmUnlocked && (
                <button
                  className={`feature-button ${coins >= 1000000 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('farm')}
                  disabled={coins < 1000000}
                >
                  Purchase (1,000,000 🪙)
                </button>
              )}
              {isFarmUnlocked && (
                <button className="feature-button max-level" disabled>
                  Unlocked ✓
                </button>
              )}
            </div>

            {/* Mahjong Item */}
            <div className="feature-item">
              <div className="feature-header">
                <span className="feature-icon">🀄</span>
                <div className="feature-info">
                  <h3>Can we please play mahjong now?</h3>
                  <p className="feature-description">
                    {mahjongPurchased 
                      ? 'Yes, you can play mahjong now!' 
                      : 'Unlock the ability to play mahjong!'}
                  </p>
                </div>
              </div>
              {!mahjongPurchased && (
                <button
                  className={`feature-button ${coins >= 100 ? 'affordable' : 'unaffordable'}`}
                  onClick={() => onPurchaseFeature('mahjong')}
                  disabled={coins < 100}
                >
                  Purchase (100 🪙)
                </button>
              )}
              {mahjongPurchased && (
                <a
                  href="https://www.cerpins.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="feature-button max-level"
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                >
                  Yes
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="store-footer">
          <p className="store-coins">Your coins: {coins} 🪙</p>
        </div>
      </div>
    </div>
  );
};

export default Store;

